import { DESKTOP_SIDEBAR_COLLAPSED_STORAGE_KEY } from '@roko/ui/constants/storage'
import type { StateCreator } from 'zustand'
import { create } from 'zustand'
import type { DevtoolsOptions } from 'zustand/middleware'
import { devtools } from 'zustand/middleware'

import { THEME } from '@/constants/layout'
import { isDev } from '@/utils/env'

type TTheme = (typeof THEME)[keyof typeof THEME]

interface IState {
  themeState: TTheme
  isDesktopSidebarCollapsed: boolean
  setTheme: (theme: TTheme) => void
  setDesktopSidebarCollapsed: (collapsed: boolean) => void
  toggleDesktopSidebarCollapsed: () => void
  clearStore: () => void
}

type TDevtoolsMiddleware = <T>(config: StateCreator<T>, options: DevtoolsOptions) => StateCreator<T>

const INITIAL_STATE = {
  themeState: (localStorage.getItem('vite-ui-theme') as TTheme) ?? THEME.LIGHT,
  isDesktopSidebarCollapsed: localStorage.getItem(DESKTOP_SIDEBAR_COLLAPSED_STORAGE_KEY) === 'true',
}

export const useLayoutStore = create<IState>()(
  (devtools as TDevtoolsMiddleware)<IState>(
    (set, get) => ({
      ...INITIAL_STATE,
      setTheme: (newTheme: TTheme) => {
        localStorage.setItem('vite-ui-theme', newTheme)
        set({ themeState: newTheme })
        document.documentElement.classList.remove(THEME.LIGHT, THEME.DARK)

        if (newTheme === THEME.SYSTEM) {
          const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? THEME.DARK : THEME.LIGHT
          document.documentElement.classList.add(systemTheme)
        } else {
          document.documentElement.classList.add(newTheme)
        }
      },
      setDesktopSidebarCollapsed: (collapsed: boolean) => {
        localStorage.setItem(DESKTOP_SIDEBAR_COLLAPSED_STORAGE_KEY, String(collapsed))
        set({ isDesktopSidebarCollapsed: collapsed })
      },
      toggleDesktopSidebarCollapsed: () => {
        const next = !get().isDesktopSidebarCollapsed
        localStorage.setItem(DESKTOP_SIDEBAR_COLLAPSED_STORAGE_KEY, String(next))
        set({ isDesktopSidebarCollapsed: next })
      },
      clearStore: () => {
        set({ ...INITIAL_STATE })
      },
    }),
    { name: 'useLayoutStore', enabled: isDev() }
  )
)
